import { useCallback, useEffect, useRef, useState } from "react";

export function useDesktopWindows(initialWindows = []) {
  const [openWindows, setOpenWindows] = useState(initialWindows);
  const [focusedWindow, setFocusedWindow] = useState(initialWindows[0] || "");
  const [maximizedWindow, setMaximizedWindow] = useState("");
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [flashWindow, setFlashWindow] = useState("");
  const windowsContainerRef = useRef(null);
  const windowRefs = useRef({});
  const flashTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        window.clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  const focusAndRevealWindow = useCallback((id) => {
    setFocusedWindow(id);
    setMinimizedWindows((prev) => prev.filter((item) => item !== id));

    window.setTimeout(() => {
      const container = windowsContainerRef.current;
      const target = windowRefs.current[id];

      if (container && target) {
        const top = Math.max(0, target.offsetTop - 10);
        container.scrollTo({ top, behavior: "smooth" });
      }

      setFlashWindow(id);
      if (flashTimeoutRef.current) {
        window.clearTimeout(flashTimeoutRef.current);
      }
      flashTimeoutRef.current = window.setTimeout(() => {
        setFlashWindow("");
      }, 900);
    }, 0);
  }, []);

  const openWindow = useCallback(
    (id) => {
      setOpenWindows((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setMinimizedWindows((prev) => prev.filter((item) => item !== id));
      focusAndRevealWindow(id);
      setStartMenuOpen(false);
    },
    [focusAndRevealWindow]
  );

  const closeWindow = useCallback(
    (id) => {
      setOpenWindows((prev) => {
        const next = prev.filter((item) => item !== id);
        if (focusedWindow === id) {
          setFocusedWindow(next[next.length - 1] || "");
        }
        if (maximizedWindow === id) {
          setMaximizedWindow("");
        }
        setMinimizedWindows((minimized) => minimized.filter((item) => item !== id));
        return next;
      });
    },
    [focusedWindow, maximizedWindow]
  );

  const toggleMaximizeWindow = useCallback((id) => {
    setMinimizedWindows((prev) => prev.filter((item) => item !== id));
    setMaximizedWindow((current) => (current === id ? "" : id));
    setFocusedWindow(id);
  }, []);

  const toggleMinimizeWindow = useCallback((id) => {
    setMinimizedWindows((prev) => {
      if (prev.includes(id)) {
        setFocusedWindow(id);
        return prev.filter((item) => item !== id);
      }
      if (maximizedWindow === id) {
        setMaximizedWindow("");
      }
      return [...prev, id];
    });
  }, [maximizedWindow]);

  return {
    openWindows,
    focusedWindow,
    maximizedWindow,
    minimizedWindows,
    startMenuOpen,
    flashWindow,
    windowsContainerRef,
    windowRefs,
    setFocusedWindow,
    setStartMenuOpen,
    focusAndRevealWindow,
    openWindow,
    closeWindow,
    toggleMaximizeWindow,
    toggleMinimizeWindow,
    setMaximizedWindow,
  };
}
